/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MaterialReactTable } from "material-react-table";
import { Box, IconButton, TextField, InputAdornment } from "@mui/material";
import SaveIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";
import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import ImmunizationsHeader from "./Header/ImmunizationsHeader";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import AssignUserModal from "components/Common/Modal/AssignUserModal";
import SaveImmunizationPopup from "./Popup/SaveImmunizationPopup";

const ImmunizationsListBody = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const moduleAccess = localStorage.getItem("moduleaccess");

  const [isLoading, setIsLoading] = useState(false);
  const [immunizationList, setImmunizationList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedImmunizationIds, setSelectedImmunizationIds] = useState([]);
  const [selectedImmunizationId, setSelectedImmunizationId] = useState(null);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Inline editing state
  const [editingCell, setEditingCell] = useState({ rowId: null, column: null });
  const [editingValue, setEditingValue] = useState("");

  // Get all immunizations
  const getAllImmunizations = async () => {
    try {
      setIsLoading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_IMMUNIZATION + `?token=${token}`;

      console.log("requestUrl", requestUrl);
      const response = await getData(requestUrl);
      console.log("response in immunization list------->", response);
      setIsLoading(false);
      if (response.status) {
        setImmunizationList(response.data);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // Bulk update immunizations
  const updateBulkImmunizationHandler = async () => {
    if (selectedImmunizationIds.length > 0) {
      try {
        const immunizationListToBeUpdate = immunizationList
          .filter((item) => selectedImmunizationIds.includes(item._id))
          .map((item) => ({
            _id: item._id,
            vaccinename: item.vaccinename,
            dateadministered: item.dateadministered,
            dosenumber: item.dosenumber,
            status: item.status,
            administeredby: item.administeredby,
            facility: item.facility,
            batchnumber: item.batchnumber,
            sideeffects: item.sideeffects,
            assignto: item.assignto,
          }));
        let immunizationData = { immunizationlist: immunizationListToBeUpdate };
        let requestURL =
          url.API_BASE_URL +
          url.API_UPDATE_BULK_IMMUNIZATION +
          `?token=${token}`;
        const response = await putData(requestURL, immunizationData);

        console.log("response in update bulk immunization handler", response);
        if (response.status) {
          setMessageType("success");
          setTimeout(() => {
            setSelectedImmunizationIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }
        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one immunization");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // Bulk delete immunizations
  const deleteBulkImmunizationHandler = async () => {
    if (selectedImmunizationIds.length > 0) {
      try {
        const immunizationListToBeDelete = immunizationList.filter((item) =>
          selectedImmunizationIds.includes(item._id)
        );
        let immunizationData = { immunizationlist: immunizationListToBeDelete };
        let requestURL =
          url.API_BASE_URL +
          url.API_DELETE_BULK_IMMUNIZATION +
          `?token=${token}`;
        const response = await putData(requestURL, immunizationData);
        if (response.status) {
          setMessageType("success");
          setTimeout(() => {
            setImmunizationList((prevList) =>
              prevList.filter(
                (item) => !selectedImmunizationIds.includes(item._id)
              )
            );
            setSelectedImmunizationIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }
        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one immunization");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // After any action
  const afterActionClose = (data = []) => {
    setImmunizationList((prevList) => mergeListById(prevList, data));
  };

  useEffect(() => {
    getAllImmunizations();
  }, []);

  // Save cell edits
  const handleSaveCellEdit = (rowId, column, value, extra = {}) => {
    setImmunizationList((prevList) =>
      prevList.map((item) => {
        if (item._id !== rowId) return item;
        if (column === "dateadministered") {
          return {
            ...item,
            dateadministered: extra.isoDate || "",
            dateadministereddate: extra.formattedDate || "",
          };
        }
        if (column === "status") {
          return { ...item, status: value?.value || value?.label || "" };
        }
        return { ...item, [column]: value };
      })
    );
    setEditingCell({ rowId: null, column: null });
    setEditingValue("");
    setRowSelection((prevSelection) => ({
      ...prevSelection,
      [rowId]: true,
    }));
    setSelectedImmunizationIds((prevIds) =>
      prevIds.includes(rowId) ? prevIds : [...prevIds, rowId]
    );
  };

  const handleCancelEdit = () => {
    setEditingCell({ rowId: null, column: null });
    setEditingValue("");
  };

  // Utility to parse dateadministered to ISO string safely
  function getValidISODateString(dateStr) {
    if (!dateStr) return "";
    // If already ISO format
    if (/^\d{4}-\d{2}-\d{2}T/.test(dateStr)) {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
    }
    // If YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
    }
    // If DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split("-");
      const d = new Date(`${year}-${month}-${day}`);
      return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
    }
    return "";
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveImmunizationOffCanvas"
            aria-controls="saveImmunizationOffCanvas"
            onClick={() => {
              setSelectedImmunizationId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableSorting: false,
      },
      {
        header: "Vaccine Name",
        accessorKey: "vaccinename",
        size: 200,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "vaccinename" ? (
            <TextField
              size="small"
              value={editingValue || row.original.vaccinename || ""}
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveCellEdit(
                          row.original._id,
                          "vaccinename",
                          editingValue
                        )
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                      disabled={!editingValue}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveCellEdit(
                    row.original._id,
                    "vaccinename",
                    editingValue
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "vaccinename",
                });
                setEditingValue(row.original.vaccinename || "");
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.vaccinename ? (
                row.original.vaccinename
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Date Administered",
        accessorKey: "dateadministered",
        size: 160,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "dateadministered" ? (
            <TextField
              size="small"
              type="date"
              value={
                editingValue ||
                getValidISODateString(row.original.dateadministered)
              }
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const isoDate = editingValue
                          ? new Date(editingValue).toISOString()
                          : "";
                        const formattedDate = editingValue
                          ? getDateFormatInSpanish(editingValue)
                          : "";
                        handleSaveCellEdit(
                          row.original._id,
                          "dateadministered",
                          editingValue,
                          { isoDate, formattedDate }
                        );
                      }}
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const isoDate = editingValue
                    ? new Date(editingValue).toISOString()
                    : "";
                  const formattedDate = editingValue
                    ? getDateFormatInSpanish(editingValue)
                    : "";
                  handleSaveCellEdit(
                    row.original._id,
                    "dateadministered",
                    editingValue,
                    { isoDate, formattedDate }
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <div
              className="joined border-bottom-0"
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "dateadministered",
                });
                setEditingValue(
                  getValidISODateString(row.original.dateadministered)
                );
              }}
              style={{ cursor: "pointer" }}
            >
              <p className="d-flex align-items-center gap-2">
                <span className="d-block material-symbols-outlined icon-md">
                  calendar_month
                </span>
                <span className="d-block">
                  {getDateFormatInSpanish(
                    getValidISODateString(row.original.dateadministered)
                  )}
                </span>
              </p>
            </div>
          ),
      },
      {
        header: "Dose Number",
        accessorKey: "dosenumber",
        size: 120,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "dosenumber" ? (
            <TextField
              size="small"
              type="number"
              value={editingValue || row.original.dosenumber || ""}
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveCellEdit(
                          row.original._id,
                          "dosenumber",
                          editingValue
                        )
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                      disabled={!editingValue}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveCellEdit(
                    row.original._id,
                    "dosenumber",
                    editingValue
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "dosenumber",
                });
                setEditingValue(row.original.dosenumber || "");
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.dosenumber ? (
                row.original.dosenumber
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Status",
        accessorKey: "status",
        Cell: ({ row }) => {
          const statusText =
            row.original.nextdoseduedate === "" ? "Received" : "Pending";
          const statusColorClass =
            row.original.nextdoseduedate === ""
              ? "text-success"
              : "text-danger";

          const statusIcon =
            row.original.nextdoseduedate === "" ? "check_circle" : "cancel";
          return (
            <p
              className={`fs-sm fw-semibold d-flex align-items-center ${statusColorClass}`}
            >
              <span
                className={`material-symbols-outlined icon-sm me-1 ${statusColorClass}`}
              >
                {statusIcon}
              </span>
              {statusText}
            </p>
          );
        },
      },
      {
        header: "Administered By",
        accessorKey: "administeredby",
        size: 180,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "administeredby" ? (
            <TextField
              size="small"
              value={editingValue || row.original.administeredby || ""}
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveCellEdit(
                          row.original._id,
                          "administeredby",
                          editingValue
                        )
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                      disabled={!editingValue}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveCellEdit(
                    row.original._id,
                    "administeredby",
                    editingValue
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "administeredby",
                });
                setEditingValue(row.original.administeredby || "");
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.administeredby ? (
                row.original.administeredby
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Facility",
        accessorKey: "facility",
        size: 250,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "facility" ? (
            <TextField
              size="small"
              value={editingValue || row.original.facility || ""}
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveCellEdit(
                          row.original._id,
                          "facility",
                          editingValue
                        )
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                      disabled={!editingValue}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveCellEdit(
                    row.original._id,
                    "facility",
                    editingValue
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "facility",
                });
                setEditingValue(row.original.facility || "");
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.facility ? (
                row.original.facility
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Batch Number",
        accessorKey: "batchnumber",
        size: 120,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "batchnumber" ? (
            <TextField
              size="small"
              value={editingValue || row.original.batchnumber || ""}
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveCellEdit(
                          row.original._id,
                          "batchnumber",
                          editingValue
                        )
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                      disabled={!editingValue}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveCellEdit(
                    row.original._id,
                    "batchnumber",
                    editingValue
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "batchnumber",
                });
                setEditingValue(row.original.batchnumber || "");
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.batchnumber ? (
                row.original.batchnumber
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Side Effects",
        accessorKey: "sideeffects",
        size: 120,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "sideeffects" ? (
            <TextField
              size="small"
              value={editingValue || row.original.sideeffects || ""}
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveCellEdit(
                          row.original._id,
                          "sideeffects",
                          editingValue
                        )
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                      disabled={!editingValue}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveCellEdit(
                    row.original._id,
                    "sideeffects",
                    editingValue
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "sideeffects",
                });
                setEditingValue(row.original.sideeffects || "");
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.sideeffects ? (
                row.original.sideeffects
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Assigned To",
        accessorKey: "assignedpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.assignedpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assignedpersonimage == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assignedpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.assignedpersonname}</span>
            </p>
          </div>
        ),
      },
    ],
    [editingCell, editingValue]
  );

  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ];

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    setSelectedImmunizationIds(selectedIdsArray);
  }, [rowSelection]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    (moduleAccess && moduleAccess.includes("MOD_APPOINTMENT_MANAGEMENT"))
  ) {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Immunizations")}
          />
          <div className="container-fluid px-lg-5">
            <ImmunizationsHeader
              reloadList={getAllImmunizations}
              selectedItemIds={selectedImmunizationIds}
              updateBulkHandler={updateBulkImmunizationHandler}
            />
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={immunizationList}
                  enableGrouping
                  enableRowSelection
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  initialState={{ rowSelection, columnOrder }}
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 300,
                    size: 50,
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>
          {/* popup and modals */}

          <SaveImmunizationPopup
            selectedImmunizationId={selectedImmunizationId}
            setSelectedImmunizationId={setSelectedImmunizationId}
            afterPopupClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          <AssignUserModal
            moduleName="immunization"
            afterModalClose={afterActionClose}
            selectedIds={selectedImmunizationIds}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedImmunizationIds([]);
            }}
          />

          <DeleteWarningModal
            onDelete={deleteBulkImmunizationHandler}
            onCancelDelete={() => {
              setRowSelection({});
              setSelectedImmunizationIds([]);
            }}
            moduleName={t("immunization")}
          />
          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
      </div>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default ImmunizationsListBody;
